import { AfterViewInit, Component, computed, effect, inject, Inject, Input, OnDestroy, signal, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgbAccordionDirective, NgbAccordionModule, NgbDropdownModule, NgbModal, NgbModalModule } from "@ng-bootstrap/ng-bootstrap";
import { ProjectsComponent, DownloadStatus } from "./projects.component";
import { ProjectLayersComponent } from "./project-layers.component";
import { ProjectPointsService } from "./project-points.service";
import { ProjectRegionsComponent } from "./project-regions.component";
import { LayerStoreComponent } from "../layer-store/layer-store.component";
import { PointStoreComponent } from "../point-store/point-store.component";
import { LayerService } from "../services/layer.service";
import { OfflineLayerService } from "../services/offline-layer.service";
import { MapStatusService } from "../services/map-status.service";
import { OfflineModeService } from "../../services/offline-mode.service";
import { PROJECT_PROVIDER, DATA_PROVIDER, CONFIRM_SERVICE, IConfirmService, SYNC_PROGRESS_COMPONENT, AUTH_PROVIDER, AuthProvider, PROJECT_ROLES_CONFIG, ProjectRolesConfig, PROJECT_EXTRA_ACTIONS, ProjectExtraAction, PROJECT_ENTITY_LABEL, PROJECT_DELETION_HANDLER, ProjectDeletionHandler } from "../../core";
import { BehaviorSubject, Subject, takeUntil } from "rxjs";
import { ProjectNewComponent } from "./new/project-new.component";
import { SyncOptionsModalComponent, SyncOption } from "./sync-options-modal.component";

@Component({
    selector: 'bp-project-manager',
    templateUrl: 'project-manager.component.html',
    styleUrls: ['project-manager.component.css'],
    imports: [CommonModule, NgbAccordionModule, NgbDropdownModule, ProjectsComponent, ProjectLayersComponent, ProjectRegionsComponent, NgbModalModule],
    standalone: true
})
export class ProjectManagerComponent implements AfterViewInit, OnDestroy {
	@ViewChild('accordion') accordion!: NgbAccordionDirective;
	@ViewChild('projectsComp') projectsComp?: ProjectsComponent;

	/** Wenn true, wird nur die Layer-Sektion angezeigt (ohne Accordion) */
	@Input() layersOnly = false;

	private expandedSection = new BehaviorSubject<string | null>(null);
	private destroy$ = new Subject<void>();

	// Ist die Projekte-Section collapsed?
	projectsSectionCollapsed = signal<boolean>(false);

	// Zuklapp-Status für Bereiche und Layer (wird im ProjectService gespeichert)
	regionsSectionCollapsed = signal<boolean>(false);
	layersSectionCollapsed = signal<boolean>(false);
	private accordionStateLoaded = false;

	// Download-Status für aktives Projekt
	activeProjectDownloadStatus = signal<DownloadStatus>('none');
	// True wenn Download-Status initial ermittelt wurde
	downloadStatusInitialized = signal<boolean>(false);
	// True während Modus-Wechsel vorbereitet wird (sofortiges Feedback im Toggle)
	isSwitchingMode = signal<boolean>(false);

	// Online: aktiv wenn nicht im Offline-Modus
	onlineActive = computed(() => !this.oms.offlineMode());
	// Offline: verfügbar wenn aktives Projekt vollständig heruntergeladen ist (oder nur Geodaten fehlen)
	offlineAvailable = computed(() => {
		const status = this.activeProjectDownloadStatus();
		return status === 'complete' || status === 'geodata_missing';
	});
	// Offline: aktiv wenn im Offline-Modus
	offlineActive = computed(() => this.oms.offlineMode());
	// Hat Projektbereiche: Button soll sichtbar sein wenn Regions definiert sind
	hasProjectRegions = computed(() => {
		const regions = this.ls.regions()?.regions;
		return regions && regions.length > 0;
	});

	// Kombinierter Loading-Status: Punkte, Download-Status oder Tiles werden geladen
	isLoading = computed(() => this.pps.isLoading() || !this.downloadStatusInitialized() || this.mss.tilesLoading() > 0);

	extraActions = inject(PROJECT_EXTRA_ACTIONS);
	entityLabel = inject(PROJECT_ENTITY_LABEL);
	private deletionHandler = inject(PROJECT_DELETION_HANDLER, { optional: true }) as ProjectDeletionHandler | null;

	constructor(
		@Inject(PROJECT_PROVIDER) public ps: any,
		private modalService: NgbModal,
		private ls: LayerService,
		private ols: OfflineLayerService,
		@Inject(DATA_PROVIDER) private ds: any,
		public oms: OfflineModeService,
		public pps: ProjectPointsService,
		@Inject(CONFIRM_SERVICE) private confirmService: IConfirmService,
		@Inject(SYNC_PROGRESS_COMPONENT) private syncProgressComponent: any,
		@Inject(AUTH_PROVIDER) private auth: AuthProvider,
		public mss: MapStatusService,
		@Inject(PROJECT_ROLES_CONFIG) public rolesConfig: ProjectRolesConfig
	) {
		effect(() => {
			const projectId = this.activeProject();
			if (projectId) {
				// Status zurücksetzen bei Projekt-Wechsel
				this.downloadStatusInitialized.set(false);
				this.accordionStateLoaded = false;
				this.ps.getProjectValue('lastOpenedSection').then((sectionId: string) => {
					if (sectionId) {
						this.expandedSection.next(sectionId);
					}
				});
				// Akkordeon-Zustand laden
				this.loadAccordionState();
				this.updateDownloadStatus();
			} else {
				this.downloadStatusInitialized.set(false);
				this.accordionStateLoaded = false;
			}
		});

		// Status aktualisieren bei relevanten Events
		// WICHTIG: Nur Events die den Status WIRKLICH ändern sollten ihn neu berechnen
		// Download abgeschlossen -> Status verbessert sich (wird in updateDownloadStatus geprüft)
		this.ols.seedAllCompleted.pipe(takeUntil(this.destroy$)).subscribe(() => {
			console.log('[PM] seedAllCompleted -> updateDownloadStatus');
			this.updateDownloadStatus();
		});
		this.ds.pointsDownloadCompleted.pipe(takeUntil(this.destroy$)).subscribe(() => {
			console.log('[PM] pointsDownloadCompleted -> updateDownloadStatus');
			this.updateDownloadStatus();
		});
		// Daten verworfen -> Status auf 'none' setzen
		this.ds.downloadedPointsDiscarded.pipe(takeUntil(this.destroy$)).subscribe(() => {
			console.log('[PM] downloadedPointsDiscarded -> status=none');
			this.activeProjectDownloadStatus.set('none');
		});
		this.ps.discarded.pipe(takeUntil(this.destroy$)).subscribe(() => {
			console.log('[PM] ps.discarded -> status=none');
			this.activeProjectDownloadStatus.set('none');
		});
		// Layer/Regions definiert -> nur beim ersten Mal berechnen (wenn noch nicht initialisiert)
		this.ls.layersDefined.pipe(takeUntil(this.destroy$)).subscribe(() => {
			console.log('[PM] layersDefined, initialized:', this.downloadStatusInitialized());
			if (!this.downloadStatusInitialized()) {
				this.updateDownloadStatus();
			}
		});
		// Regions geändert -> Neuberechnung nötig (Download-Anforderungen ändern sich)
		this.ls.regionsChanged.pipe(takeUntil(this.destroy$)).subscribe(() => {
			console.log('[PM] regionsChanged -> updateDownloadStatus');
			this.updateDownloadStatus();
		});
		// Attachments geändert -> Neuberechnung für Geodaten-Status
		this.ls.attachmentsChanged.pipe(takeUntil(this.destroy$)).subscribe(() => {
			console.log('[PM] attachmentsChanged -> updateDownloadStatus');
			this.updateDownloadStatus();
		});
		// Nur in der vollständigen Version das Modal nach Bereichszeichnung öffnen (nicht in layersOnly)
		// Wird in ngAfterViewInit aktiviert, nachdem layersOnly-Input gesetzt ist
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	activeProject = computed((): string => {
		return this.ps.project();
	});

	activeProjectName = computed((): string => {
		const projectId = this.ps.project();
		if (!projectId) return '';
		const project = this.ps.projectList().find((p: any) => p._id === projectId);
		return project?.name ?? '';
	});

	isGuest = computed((): boolean => this.auth.amI('gast'));
	isNotAuthenticated = computed((): boolean => !this.auth.isAuthenticated.value);
	readonly guestHint = 'Für Gäste nicht verfügbar';
	readonly notAuthenticatedHint = 'Bitte anmelden';
	readonly networkHint = 'Keine Netzwerkverbindung';

	/** Prüft ob Netzwerk im Online-Modus nicht verfügbar ist */
	networkUnavailable = computed((): boolean => !this.oms.userOfflineMode() && !this.oms.networkOnline());

	/** Prüft ob Aktion blockiert ist (nicht angemeldet ODER Gast ODER kein Netzwerk im Online-Modus) */
	isActionBlocked = computed((): boolean => this.isNotAuthenticated() || this.isGuest() || this.networkUnavailable());

	/** Tooltip für blockierte Aktionen */
	getBlockedHint(): string {
		if (this.networkUnavailable()) return this.networkHint;
		if (this.isNotAuthenticated()) return this.notAuthenticatedHint;
		if (this.isGuest()) return this.guestHint;
		return '';
	}

	/** Prüft ob ein Projekt Mitglieder in irgendeiner Rolle hat */
	hasAnyMembers(prj: any): boolean {
		return this.rolesConfig.memberRoles.some(r => prj[r.field]?.length);
	}

	/**
	 * Formatiert eine Liste von Projektmitgliedern für die Anzeige.
	 * Zeigt max. 2 Namen an, danach "+N".
	 */
	formatMembers(members: { username: string }[] | undefined): string {
		if (!members || members.length === 0) return '';
		if (members.length <= 2) {
			return members.map(m => m.username).join(', ');
		}
		return `${members[0].username}, ${members[1].username} +${members.length - 2}`;
	}

	projectList = computed(() => {
		// Projekte ohne Namen ausfiltern
		return this.ps.projectList().filter((p: any) => p.name?.trim());
	});

	/**
	 * Gibt den kombinierten Status für ein Projekt zurück:
	 * - 'pending': Lokale Änderungen vorhanden (nur für aktuelles Projekt)
	 * - 'complete': Offline verfügbar
	 * - 'partial': Teilweise heruntergeladen
	 * - 'geodata_missing': Geodaten fehlen
	 * - 'none': Nicht offline verfügbar
	 */
	getProjectStatus(projectId: string): string {
		// Pending-Status nur für aktuelles Projekt prüfen (Punkte oder Projekt-Daten)
		if (projectId === this.ps.project() && (this.ds.hasPendingChanges() || this.ps.hasPendingChanges())) {
			return 'pending';
		}
		// Für aktives Projekt: Lokalen Status verwenden (immer aktuell)
		if (projectId === this.ps.project()) {
			return this.activeProjectDownloadStatus();
		}
		// Für andere Projekte: Direkt aus ProjectsComponent lesen
		const status = this.projectsComp?.projectDownloadStatus().get(projectId);
		return status ?? 'none';
	}

	projectsHeaderText = computed((): string => {
		const projectId = this.ps.project();
		if (!projectId) return `${this.entityLabel.name}: ${this.entityLabel.noneLoaded}`;
		const project = this.ps.projectList().find((p: any) => p._id === projectId);
		return `${this.entityLabel.name}: ` + (project?.name ?? this.entityLabel.noneLoaded);
	});

	private async updateDownloadStatus(): Promise<void> {
		const projectId = this.activeProject();
		console.log('[PM] updateDownloadStatus called, projectId:', projectId);
		if (!projectId) {
			this.activeProjectDownloadStatus.set('none');
			this.downloadStatusInitialized.set(true);
			return;
		}

		// Verwende aktuelle In-Memory-Werte aus LayerService für konsistente Status-Berechnung
		// Nur aktive Bereiche für Status-Berechnung (konsistent mit Download)
		const allRegions = this.ls.regions()?.regions ?? [];
		const activeIndices = this.pps.activeRegionIndices();
		const activeRegions = activeIndices.length > 0
			? activeIndices.filter(i => i < allRegions.length).map(i => allRegions[i])
			: allRegions;  // Fallback: alle Bereiche wenn keine explizit aktiv
		const regionBounds = activeRegions.map(r => r.bounds);
		const mapDef = this.ls.mapDef();

		// Wenn mapDef noch nicht geladen ist, Status noch nicht initialisieren
		// (wird durch layersDefined Event erneut aufgerufen)
		if (!mapDef || !mapDef.layers || mapDef.layers.length === 0) {
			// Nicht downloadStatusInitialized setzen - warten auf layersDefined
			console.log('[PM] mapDef not ready, waiting');
			return;
		}

		// Alle Status-Abfragen parallel ausführen (nur lokaler Storage-Zugriff)
		const [tilesStatus, geodataStatus, pointsDownloaded] = await Promise.all([
			this.ols.getDownloadStatusForProject(projectId, regionBounds, mapDef),
			this.ols.getGeodataFilesStatus(projectId, mapDef),
			this.ds.arePointsDownloadedForProject(projectId)
		]);
		console.log('[PM] status check - tiles:', tilesStatus, 'geodata:', geodataStatus, 'points:', pointsDownloaded);

		// Kombiniere Status: Tiles + Punkte + Geodaten
		let combinedStatus: DownloadStatus = tilesStatus;

		// Tiles + Punkte Kombination
		if (tilesStatus === 'complete' && !pointsDownloaded) {
			combinedStatus = 'partial';
		} else if (tilesStatus === 'none' && pointsDownloaded) {
			combinedStatus = 'partial';
		}

		// Geodaten-Status berücksichtigen
		if (geodataStatus !== 'not_applicable') {
			if (combinedStatus === 'complete' && geodataStatus !== 'complete') {
				// Tiles+Punkte complete aber Geodaten fehlen → spezieller Status
				combinedStatus = 'geodata_missing';
			} else if (combinedStatus !== 'none' && geodataStatus === 'none') {
				combinedStatus = 'partial';
			} else if (geodataStatus === 'partial') {
				if (combinedStatus === 'complete') {
					combinedStatus = 'geodata_missing';
				} else if (combinedStatus !== 'none') {
					combinedStatus = 'partial';
				}
			}
		}

		console.log('[PM] setting combinedStatus:', combinedStatus);
		this.activeProjectDownloadStatus.set(combinedStatus);
		this.downloadStatusInitialized.set(true);
	}

	async toggleOfflineMode(): Promise<void> {
		if (this.offlineActive()) {
			// Wird nicht mehr direkt verwendet - siehe onSync() und onGoOnline()
			return;
		}

		// Immer Download-Dialog öffnen - dort wird geprüft ob neue Punkte/Tiles nötig sind
		// und nach erfolgreichem Download kann direkt in Offline-Modus gewechselt werden
		await this.projectsComp?.onDownload();
	}

	/**
	 * Zeigt einen Dialog mit drei Optionen:
	 * - Synchronisieren und online gehen
	 * - Nur synchronisieren (offline bleiben)
	 * - Änderungen verwerfen
	 */
	async onSync(): Promise<void> {
		if (!this.offlineActive()) return;

		// Optionen-Dialog öffnen
		const modalRef = this.modalService.open(SyncOptionsModalComponent);
		modalRef.componentInstance.networkOnline = this.oms.networkOnline();
		let action: SyncOption;
		try {
			action = await modalRef.result;
		} catch {
			return; // Dialog wurde geschlossen
		}
		if (action === 'cancel') return;

		if (action === 'sync-online' || action === 'sync-offline') {
			// Kurz warten damit async Operationen (z.B. setProjectValue) abschließen können
			await new Promise(resolve => setTimeout(resolve, 100));

			// Prüfen ob überhaupt lokale Daten vorhanden sind (persistenter Check)
			const hasLocalPoints = await this.ds.arePointsDownloadedForProject(this.ps.project());
			const hasPendingProject = this.ps.hasPendingChanges();

			if (!hasLocalPoints && !hasPendingProject) {
				// Keine lokalen Daten - nur Info-Meldung, kein Progress-Dialog
				await this.confirmService.alert('Es gibt keine Änderungen zu synchronisieren.');
				if (action === 'sync-online') {
					// Nur Offline-Modus verlassen, Daten behalten
					await this.oms.setOfflineMode(false);
					this.ls.offlineModeChanged.emit(false);
				}
				return;
			}

			// Sync-Status setzen bevor Modal geöffnet wird (verhindert kurze "Keine Daten"-Anzeige)
			this.ds.isSyncing.set(true);

			// Sync-Fortschritt-Dialog öffnen
			const progressRef = this.modalService.open(this.syncProgressComponent, {
				backdrop: 'static',
				keyboard: false
			});

			// Daten synchronisieren (lokal → remote) - nur aktuelles Projekt
			let projectSyncOk = false;
			let pointsSyncOk = false;
			try {
				projectSyncOk = await this.ps.syncProjectToRemote();
				pointsSyncOk = await this.ds.syncCurrentProjectToRemote();
			} catch (error) {
				console.error('Sync-Fehler:', error);
			} finally {
				// isSyncing IMMER zurücksetzen, damit der Dialog schließbar wird
				this.ds.isSyncing.set(false);
			}

			// Warten bis Modal geschlossen wird
			await progressRef.result;

			if (!projectSyncOk || !pointsSyncOk) {
				await this.confirmService.error(
					'Beim Synchronisieren sind Fehler aufgetreten. '
					+ 'Möglicherweise war keine Serververbindung möglich. '
					+ 'Die lokalen Daten sind weiterhin vorhanden.'
				);
				return;
			}

			if (action === 'sync-online') {
				// Nach Sync: Nur Offline-Modus verlassen, Daten behalten für späteren Offline-Einsatz
				await this.oms.setOfflineMode(false);
				this.ls.offlineModeChanged.emit(false);
			}
		} else if (action === 'discard') {
			// Nur Offline-Modus verlassen, Daten behalten für späteren Offline-Einsatz
			await this.oms.setOfflineMode(false);
			this.ls.offlineModeChanged.emit(false);
		}
	}

	/**
	 * Gibt die IDs aller vollständig heruntergeladenen Projekte zurück.
	 */
	private getCompleteProjectIds(): string[] {
		const statusMap = this.projectsComp?.projectDownloadStatus() ?? new Map();
		const completeIds: string[] = [];
		statusMap.forEach((status, projectId) => {
			if (status === 'complete') {
				completeIds.push(projectId);
			}
		});
		return completeIds;
	}


	ngAfterViewInit(): void {
		this.expandedSection.pipe(takeUntil(this.destroy$)).subscribe((sectionId: string | null) => {
			if (sectionId) {
				this.accordion.expand(sectionId);
			}
		});

		// Nur in der vollständigen Version (nicht layersOnly) das Modal nach Bereichszeichnung öffnen
		if (!this.layersOnly) {
			this.ls.regionDrawingCompleted.pipe(takeUntil(this.destroy$)).subscribe(() => this.openPointStore('regions'));
		}
	}

	selectProject(projectName: string): void {
		if (projectName)
			this.ps.loadProject(projectName);
		else
			this.ps.unloadCurrentProject();
	}

	onLayersOpened($event: Event): void {
		const modalRef = this.modalService.open(LayerStoreComponent, { scrollable: true, backdrop: 'static' });
		modalRef.componentInstance.project = this.ps.project();
		modalRef.closed.subscribe((data: any) => {
			this.ls.saveLayersToLocalStorage(data);
		});
		$event.stopPropagation();
	}

	onPointsOpened($event: Event): void {
		this.openPointStore('points');
		$event.stopPropagation();
	}

	onRegionsOpened($event: Event): void {
		this.openPointStore('regions');
		$event.stopPropagation();
	}

	private openPointStore(mode: 'points' | 'regions'): void {
		const modalRef = this.modalService.open(PointStoreComponent, { size: 'lg' });
		const instance = modalRef.componentInstance as PointStoreComponent;
		instance.mode = mode;
		instance.setParentSignals(
			this.pps.boundsSelections,
			this.pps.activeRegionIndices,
			this.pps.regionPointIds,
			this.pps.regionFilters,
			this.pps.regionNames,
			this.pps.globalFilterName,
			this.pps.useGlobalFilter
		);
		// Bei Filter-Änderungen sofort speichern
		instance.filtersChanged.subscribe(async () => {
			await this.pps.saveSelection(false);
			// Karte aktualisieren (Namen könnten geändert worden sein)
			this.ls.regionNamesChanged.emit();
		});
		// GeoJSON-Import angefordert (mit bereits ausgewählten Dateien)
		instance.geoJsonImportRequested.subscribe((files: File[]) => {
			this.ls.geoJsonImportRequested.emit(files);
		});
		modalRef.closed.subscribe(async () => {
			// Gefilterte Punkt-IDs neu berechnen falls Filter geändert wurden
			await this.pps.updateFilteredBoundsPointIds();
			this.pps.saveSelection();
		});
	}

	onSectionShown(id: string): void {
		if (id === 'projects-panel') {
			this.projectsSectionCollapsed.set(false);
		}
		if (this.activeProject()) {
			this.ps.setProjectValue('lastOpenedSection', id);
		}
	}

	onSectionHidden(id: string): void {
		if (id === 'projects-panel') {
			this.projectsSectionCollapsed.set(true);
		}
	}

	/** Akkordeon-Zustand (Bereiche/Layer collapsed) speichern */
	saveAccordionState(): void {
		if (!this.activeProject() || !this.accordionStateLoaded) return;
		this.ps.setProjectValue('accordionState', {
			regionsCollapsed: this.regionsSectionCollapsed(),
			layersCollapsed: this.layersSectionCollapsed()
		});
	}

	/** Akkordeon-Zustand laden */
	private async loadAccordionState(): Promise<void> {
		const state = await this.ps.getProjectValue('accordionState');
		if (state) {
			this.regionsSectionCollapsed.set(state.regionsCollapsed ?? false);
			this.layersSectionCollapsed.set(state.layersCollapsed ?? false);
		} else {
			// Default: beide offen
			this.regionsSectionCollapsed.set(false);
			this.layersSectionCollapsed.set(false);
		}
		this.accordionStateLoaded = true;
	}

	/** Toggle für Bereiche-Sektion mit Speicherung */
	toggleRegionsSection(): void {
		this.regionsSectionCollapsed.set(!this.regionsSectionCollapsed());
		this.saveAccordionState();
	}

	/** Toggle für Layer-Sektion mit Speicherung */
	toggleLayersSection(): void {
		this.layersSectionCollapsed.set(!this.layersSectionCollapsed());
		this.saveAccordionState();
	}

	onDownload(): void {
		this.projectsComp?.onDownload();
	}

	onProjectEdit(): void {
		const activeId = this.activeProject();
		if (!activeId) return;
		const project = this.ps.projectList().find((p: any) => p._id === activeId);
		if (!project) return;

		const modalRef = this.modalService.open(ProjectNewComponent, { size: 'lg' });
		modalRef.componentInstance.setEditMode(project);
	}

	onProjectNew(): void {
		const modalRef = this.modalService.open(ProjectNewComponent, { size: 'lg' });
		modalRef.closed.subscribe(async (result: any) => {
			if (result?.action === 'created' && result?.projectId) {
				// Neues Projekt laden
				this.selectProject(result.projectId);
			}
		});
	}

	async onProjectDelete(): Promise<void> {
		const activeId = this.activeProject();
		if (!activeId) return;

		const project = this.ps.projectList().find((p: any) => p._id === activeId);
		const projectName = project?.name ?? this.entityLabel.unnamed;

		const confirmed = await this.confirmService.confirmDelete(
			`Möchten Sie ${this.entityLabel.accusativeArticle} "${projectName}" wirklich löschen?\n\nDies kann nicht rückgängig gemacht werden.`,
			`${this.entityLabel.name} löschen`
		);
		if (!confirmed) return;

		try {
			const success = this.deletionHandler
				? await this.deletionHandler.deleteProject(activeId)
				: await this.ps.deleteProject(activeId);
			if (!success) {
				await this.confirmService.error(`Fehler beim Löschen`);
			}
			this.selectProject('');
		} catch (e) {
			await this.confirmService.error(
				`Fehler beim Löschen: ` + (e instanceof Error ? e.message : 'Unbekannter Fehler')
			);
		}
	}

	/** Lädt die Projektliste beim Öffnen des Dropdowns neu */
	onProjectDropdownOpen(isOpen: boolean): void {
		if (isOpen) {
			this.ps.refreshProjectList();
		}
	}

	/**
	 * Toggle zwischen Online- und Offline-Modus.
	 * Online → Offline: Download-Dialog öffnen
	 * Offline → Online: Sync-Dialog öffnen
	 */
	onModeToggle(): void {
		// Sofort visuelles Feedback - Toggle zeigt Loading
		this.isSwitchingMode.set(true);

		// Kurze Verzögerung damit UI updaten kann, dann async weitermachen
		setTimeout(async () => {
			try {
				if (this.offlineActive()) {
					// Offline → Online: Sync-Dialog
					await this.onSync();
				} else {
					// Online → Offline: Download-Dialog
					await this.toggleOfflineMode();
				}
			} finally {
				this.isSwitchingMode.set(false);
			}
		}, 10);
	}
}
